import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as Lucide from "lucide-react";
import { ArrowRight, CheckCircle2, MessageCircle, Phone, Star, ChevronDown, Languages } from "lucide-react";
import { Button } from "../components/ui/button";
import { CLINIC } from "../lib/clinic";
import { trackEvent } from "../lib/analytics";
import { useSEO } from "../lib/useSEO";
import { LITE_AVAILABLE, LITE_LABELS } from "../lib/serviceLanding";

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShow(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ transitionDelay: `${delay}ms` }} className={`transition-all duration-700 ease-out ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}>
      {children}
    </div>
  );
}

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={`faq-item-${idx}`} className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 py-5 text-left group"
        aria-expanded={open}
      >
        <span className="font-serif text-lg lg:text-xl text-brand-500 leading-snug group-hover:text-gold-500 transition-colors">{q}</span>
        <ChevronDown size={20} className={`text-slate-400 mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-96 pb-6" : "max-h-0"}`}>
        <p className="text-slate-600 leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

export default function ServiceLandingPage({ content }) {
  // Canonical SEO is always PT-PT (useSEO sets meta in Portuguese — preserves SEO ranking)
  useSEO({ title: content.seoTitle, description: content.seoDescription });

  // Lite translation state — only overrides hero h1/subtitle, top 3 benefits & CTA labels
  const [liteLang, setLiteLang] = useState(null); // null | "en" | "de"

  // Scroll to top on mount (each landing page is a new route)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setLiteLang(null);
  }, [content.slug]);

  const lite = liteLang && content.lite ? content.lite[liteLang] : null;
  const labels = liteLang ? LITE_LABELS[liteLang] : null;
  const h1 = lite?.h1 || content.h1;
  const heroSubtitle = lite?.subtitle || content.hero.subtitle;
  const ctaPrimaryLabel = labels?.ctaPrimary || "Marcar Consulta";
  // Benefits: override the FIRST 3 in lite mode, keep rest in PT
  const displayBenefits = lite
    ? [...lite.benefits, ...content.benefits.slice(lite.benefits.length)]
    : content.benefits;

  // Inject FAQPage Schema.org JSON-LD for rich snippets in Google
  useEffect(() => {
    const id = `faq-schema-${content.slug}`;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: content.faq.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    });
    return () => { const node = document.getElementById(id); if (node) node.remove(); };
  }, [content.slug, content.faq]);

  const waUrl = `https://wa.me/${CLINIC.whatsappRaw}?text=${encodeURIComponent(`Olá, gostaria de mais informações sobre ${content.keyword} em Portimão.`)}`;

  return (
    <div data-testid={`landing-${content.slug}`}>
      {/* LANGUAGE BANNER — discreet, sits right below the header */}
      <div data-testid="lite-language-banner" className="bg-brand-50/60 border-b border-brand-100/60 pt-32 lg:pt-36">
        <div className="container-icb py-2.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[13px]">
          <Languages size={14} className="text-brand-500/70" />
          <span className="text-brand-500/80">
            {liteLang ? labels.bannerLabel : "This page is also available in:"}
          </span>
          <button
            data-testid="lite-lang-pt"
            onClick={() => { setLiteLang(null); trackEvent("landing_lang_change", { language: "pt-PT", source: "landing_banner" }); }}
            className={`underline-offset-2 hover:text-gold-500 transition-colors ${liteLang === null ? "font-semibold text-gold-600 underline" : "text-brand-500 hover:underline"}`}
          >
            Português
          </button>
          {LITE_AVAILABLE.map((lng) => (
            <span key={lng} className="flex items-center gap-3">
              <span className="text-slate-300">|</span>
              <button
                data-testid={`lite-lang-${lng}`}
                onClick={() => { setLiteLang(lng); trackEvent("landing_lang_change", { language: lng, source: "landing_banner" }); }}
                className={`underline-offset-2 hover:text-gold-500 transition-colors ${liteLang === lng ? "font-semibold text-gold-600 underline" : "text-brand-500 hover:underline"}`}
              >
                {lng === "en" ? "English" : "Deutsch"}
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* HERO */}
      <section className="relative pb-20 lg:pb-0 lg:min-h-[680px] overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-brand-50 via-white to-white" />
        <div className="absolute top-20 -right-32 w-[28rem] h-[28rem] bg-gold-100/40 rounded-full blur-3xl -z-10" />
        <div className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] bg-brand-100/30 rounded-full blur-3xl -z-10" />

        <div className="hidden lg:block absolute inset-y-0 right-0 w-1/2 xl:w-[45%] overflow-hidden">
          <img src={content.hero.image} alt={`${content.keyword} em Portimão — Instituto Clínico do Barlavento`} className="w-full h-full object-cover object-center" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/40 via-transparent to-transparent pointer-events-none" />
        </div>

        <div className="container-icb relative grid lg:grid-cols-2 gap-10 lg:gap-16 items-center pt-12 lg:pt-28 lg:pb-28">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold-50 border border-gold-200 text-gold-700 text-xs font-medium tracking-wider uppercase mb-6">
              <Lucide.Sparkles size={14} /> {content.hero.eyebrow}
            </div>
            <h1 data-testid="landing-h1" className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-brand-500 text-balance">
              {h1}
            </h1>
            <p data-testid="landing-subtitle" className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">{heroSubtitle}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/#marcacao" data-testid="landing-cta-marcar">
                <Button size="lg" className="bg-gold-400 hover:bg-gold-500 text-brand-700 font-semibold px-8 h-12 shadow-lg shadow-gold-200 hover:-translate-y-0.5 transition-all w-full">
                  {ctaPrimaryLabel} <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("click_whatsapp", { source: "landing_hero" })}
                data-testid="landing-cta-whatsapp"
              >
                <Button size="lg" variant="outline" className="border-[#25D366] text-[#1ebf5a] hover:bg-[#25D366] hover:text-white h-12 px-8 w-full">
                  <MessageCircle size={16} className="mr-2" /> WhatsApp
                </Button>
              </a>
            </div>
            <p className="mt-6 text-sm text-slate-500">{liteLang ? labels.noteFallback : content.note}</p>
          </Reveal>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-icb max-w-3xl">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-500 mb-6">{content.intro.title}</h2>
            <p className="text-slate-600 leading-relaxed text-lg">{content.intro.body}</p>
          </Reveal>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 lg:py-28 bg-slate-50/60">
        <div className="container-icb">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-3">Benefícios</p>
              <h2 className="font-serif text-3xl md:text-4xl text-brand-500">Porquê escolher {content.keyword.toLowerCase()}</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {displayBenefits.map((b, i) => {
              const Icon = Lucide[b.icon] || Lucide.CheckCircle2;
              return (
                <Reveal key={i} delay={i * 80}>
                  <div data-testid={`benefit-${i}`} className="bg-white p-6 rounded-2xl border border-slate-100 hover:border-gold-200 hover:shadow-xl transition-all duration-300 h-full">
                    <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-500 inline-flex items-center justify-center mb-4"><Icon size={22} strokeWidth={1.5} /></div>
                    <h3 className="font-serif text-lg text-brand-500 mb-2 leading-tight">{b.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{b.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-icb">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-3">Como funciona</p>
              <h2 className="font-serif text-3xl md:text-4xl text-brand-500">O nosso processo, passo a passo</h2>
            </div>
          </Reveal>
          <div className="max-w-3xl mx-auto">
            {content.process.map((step, i) => (
              <Reveal key={i} delay={i * 60}>
                <div data-testid={`process-${i}`} className="flex gap-5 pb-8 last:pb-0 relative">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-brand-500 text-white inline-flex items-center justify-center font-serif text-lg">{i + 1}</div>
                  {i < content.process.length - 1 && <div className="absolute left-6 top-12 w-px h-full bg-slate-200 -translate-x-1/2" />}
                  <div className="pt-1 pb-2">
                    <h3 className="font-serif text-xl text-brand-500 mb-1.5">{step.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 lg:py-28 bg-slate-50/60">
        <div className="container-icb">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-3">Porquê nós</p>
              <h2 className="font-serif text-3xl md:text-4xl text-brand-500">Porque escolher a nossa clínica em Portimão</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {content.why.map((w, i) => (
              <Reveal key={i} delay={i * 100}>
                <div data-testid={`why-${i}`} className="bg-white p-7 rounded-2xl border border-slate-100 hover:border-gold-200 hover:shadow-xl transition-all duration-300 h-full">
                  <CheckCircle2 size={22} className="text-gold-500 mb-3" />
                  <h3 className="font-serif text-xl text-brand-500 mb-2">{w.title}</h3>
                  <p className="text-slate-500 leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container-icb max-w-3xl">
          <Reveal>
            <div className="bg-gradient-to-br from-brand-50 to-white border border-brand-100 rounded-2xl p-8 lg:p-10 text-center">
              <div className="flex items-center justify-center gap-2 mb-4 text-gold-400">
                {Array.from({ length: 5 }).map((_, i) => (<Star key={i} size={22} fill="currentColor" />))}
              </div>
              <p className="font-serif text-2xl md:text-3xl text-brand-500 leading-snug mb-3">A confiança de quem nos visita há mais de 20 anos.</p>
              <p className="text-slate-600 leading-relaxed mb-6">Mais de 30 avaliações no Google e Doctoralia. Veja o que os pacientes dizem sobre nós.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="https://www.google.com/search?q=instituto+clinico+do+barlavento" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_google_reviews", { source: "landing_trust" })}>
                  <Button variant="outline" className="border-brand-200 text-brand-500 hover:bg-brand-50">Reviews no Google</Button>
                </a>
                <a href="https://www.doctoralia.com.pt/clinicas/instituto-clinico-do-barlavento-medico-e-dentario" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_doctoralia", { source: "landing_trust" })}>
                  <Button variant="outline" className="border-brand-200 text-brand-500 hover:bg-brand-50">Reviews na Doctoralia</Button>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-slate-50/60">
        <div className="container-icb max-w-3xl">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-3">Perguntas Frequentes</p>
              <h2 className="font-serif text-3xl md:text-4xl text-brand-500">Tudo o que precisa de saber</h2>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="bg-white rounded-2xl border border-slate-100 px-6 lg:px-8" data-testid="faq-list">
              {content.faq.map((f, i) => (<FaqItem key={i} idx={i} q={f.q} a={f.a} />))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 lg:py-28 bg-brand-500 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10 hidden lg:block">
          <img src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=900&q=80" alt="" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="container-icb relative max-w-3xl text-center">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-5 leading-tight">Pronto para começar o seu tratamento em Portimão?</h2>
            <p className="text-slate-200/90 leading-relaxed text-lg mb-9 max-w-2xl mx-auto">
              Marque hoje uma consulta de avaliação. Receberá um diagnóstico claro, um plano de tratamento personalizado e uma estimativa de custos transparente.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
              <Link to="/#marcacao" className="flex-1" data-testid="landing-cta-final-marcar">
                <Button className="w-full bg-gold-400 hover:bg-gold-500 text-brand-700 font-semibold h-12 px-6 shadow-lg shadow-gold-900/40">
                  {ctaPrimaryLabel} <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <a href={waUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_whatsapp", { source: "landing_final" })} className="flex-1">
                <Button className="w-full bg-[#25D366] hover:bg-[#1ebf5a] text-white h-12 px-6"><MessageCircle size={16} className="mr-2" /> WhatsApp</Button>
              </a>
              <a href={`tel:${CLINIC.phoneRaw}`} onClick={() => trackEvent("click_phone", { source: "landing_final" })} className="flex-1">
                <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white/10 bg-transparent h-12 px-6"><Phone size={16} className="mr-2" /> {CLINIC.phone}</Button>
              </a>
            </div>
            <p className="mt-8 text-sm text-slate-300/80">Servimos pacientes de Portimão, Alvor, Ferragudo, Lagoa, Carvoeiro, Monchique e Lagos.</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
